import React, { useEffect, useState } from 'react'

import styled from 'styled-components'

import lodash from 'lodash'

import GlobalNotification from 'live/services/global-notification'
import LiveModal from 'live/components/ui/Modal'
import i18n from 'live/services/i18n'
import GeneralTextInput from '../../../common/components/GeneralTextInput/generalTextInput'
import {  dateToCron, mailSettingsInstanceValidator } from './SchedulingHelper'
import { Notification } from '../../../common/components/Notification/Notification'
import { MailSettingsInstance } from '../../../common/types/api/report'
import { RadioGroup } from '../../../common/components/RadioGroup'

import { Recipient } from '../../../common/types/form'


import LiveSpan from '../Content/LiveSpan'
import {
    ScheduleType,
    getScheduleOptions,
    getScheduleType,
    getScheduleTypeName,
    setScheduleTypeInMailSettingsInstance
} from './helper/helper'
import SelectRecipientGroup from '../RecipientGroups/SelectRecipientGroup'

import './styles/react-datepicker.scss'
import { getScheduleTypeComponent } from './types/getScheduleComponent'
import Toggle from '../../../common/components/SimpleInputs/Toggle'

interface ModalSchedulingProps {
    mailSettingsInstance?: MailSettingsInstance
    allRecipients: Recipient[]
    showingModal: boolean
    isNew?: boolean
    onClose: (cancel: boolean) => void
    onSave: (schedulingJob: MailSettingsInstance) => void
}

const DEFAULT_SPAN = ''

export function ModalScheduling({
    mailSettingsInstance,
    allRecipients,
    showingModal,
    isNew,
    onClose,
    onSave
}: ModalSchedulingProps): JSX.Element {
    const [draftSettings, setDraftSettings] = useState<MailSettingsInstance>(
        isNew
            ? {
                  name: '',
                  cron: dateToCron(new Date()),
                  isRecurrent: false,
                  recipientsUid: [...(allRecipients?.map(r => r.uuid) ?? [])],
                  active: true,
                  span: DEFAULT_SPAN,
                  ruleId: '',
                  isRuleAlarm: false
              }
            : { ...mailSettingsInstance }
    )

    //TODO: FIX THIS FEATURE, IT IS NOT PERSISTED (TO CREATE A FIELD IN THE BACK-END), SO IT DOES NOT WORK AS INTENDED
    const [customRecipients, setCustomRecipients] = useState<boolean>(
        !lodash.isEqual(
            allRecipients.map(r => r.uuid),
            draftSettings?.recipientsUid
        )
    )
   

    const [validations, setValidations] = useState<string[]>([])
    const [scheduleType, setScheduleType] = useState<ScheduleType>(isNew ? null : getScheduleType(draftSettings))

    useEffect(() => {
        setValidations(mailSettingsInstanceValidator(draftSettings)) 
    }, [draftSettings])

    useEffect(() => {
        setDraftSettings(() => setScheduleTypeInMailSettingsInstance(scheduleType, draftSettings))
    }, [scheduleType])

    useEffect(() => {
        if (showingModal) {
            setDraftSettings(
                isNew
                    ? {
                          name: '',
                          cron: dateToCron(new Date()),
                          isRecurrent: false,
                          recipientsUid: [...(allRecipients?.map(r => r.uuid) ?? [])],
                          active: true,
                          span: DEFAULT_SPAN,
                          ruleId: '',
                          isRuleAlarm: false
                      }
                    : { ...mailSettingsInstance }
            )
        }

        if (
            !lodash.isEqual(
                draftSettings?.recipientsUid,
                allRecipients.map(r => r.uuid)
            )
        ) {
            setCustomRecipients(true)
        } else {
            setCustomRecipients(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showingModal])

    const defaultScheduleOptions = getScheduleOptions()

    return (
        <StyledLiveModal
            key={'modalscheduling'}
            show={showingModal}
            onHide={() => onClose(false)}
            animation={false}
            className="modal-sm"
        >
            <StyledLiveModal.Header>
                <StyledLiveModal.Title>
                    {i18n(`Schedule: ${draftSettings?.name ? draftSettings.name : 'New Schedule'}`)}
                </StyledLiveModal.Title>
            </StyledLiveModal.Header>
            <StyledLiveModal.Body>
                <StyledScheduling
                    className={'scheduling'}
                    dateDisabled={draftSettings?.isRecurrent}
                    cronDisabled={!draftSettings?.isRecurrent}
                    recipientGroupsDisabled={!customRecipients}
                >
                    <div className={'scheduling'}>
                        <div className={'scheduling--scheduleInfo'}>
                            <span className="settings">Settings</span>
                            <div className="statusDiv">
                                <label>Status</label>
                                <div className="toggleStatus">
                                    <Toggle
                                        value={draftSettings.active}
                                        onChange={newValue => {
                                            const newJob = { ...draftSettings }
                                            newJob.active = newValue
                                            setDraftSettings({ ...newJob })
                                        }}
                                        titleComponent={<span>{i18n('Active')}</span>}
                                    />
                                </div>
                            </div>
                            <GeneralTextInput
                                title={i18n('Schedule Name')}
                                value={draftSettings?.name}
                                tooltip={i18n('Choose a name to identify this schedule.')}
                                placeholder={i18n('Type the name of the schedule')}
                                required={true}
                                editMode={true}
                                variant={'primary'}
                                onChange={name => {
                                    const newJob = { ...draftSettings }
                                    newJob.name = name
                                    setDraftSettings(newJob)
                                }}
                            />

                            <div>
                                <div className={'scheduling--scheduleInfo__timespanSection'}>
                                    <div className={'scheduling--scheduleInfo__timespanSection--timespan'}>
                                        <LiveSpan
                                            placeholder='Select timespan'
                                            span={draftSettings?.span}
                                            defaultSpan={DEFAULT_SPAN}
                                            setSpan={newSpan => {
                                                const newJob = { ...draftSettings }
                                                newJob.span = newSpan
                                                setDraftSettings(newJob)
                                            }}
                                        />
                                    </div>
                                    <div className={'scheduling--scheduleInfo__timespanSection--bottom'}>
                                        <span>
                                            <i className="far fa-info-circle" />
                                        </span>
                                        <p>{i18n('Select a span for the report content')}</p>
                                    </div>
                                </div>
                            </div>

                            <div className={'scheduling--scheduleInfo__schedulingSection'}>
                                <RadioGroup<ScheduleType>
                                    title="Type"
                                    required
                                    value={scheduleType}
                                    options={getScheduleOptions()}
                                    tooltipClassName="radio-group-tooltip"
                                    tooltip={
                                        <StyledRadioTypeTooltipContainer>
                                            <div className="tooltip-row">
                                                <span>{`${i18n("There are three different types of Schedule")}: `}</span>
                                            </div>
                                            <div className="tooltip-row">
                                                <b>{`${i18n("One time")}: `}</b>
                                                <span>{`${i18n("the report will be sent at the specific day and time")}; `}</span>
                                            </div>
                                            <div className="tooltip-row">
                                                <b>{`${i18n("Recurrent")}: `}</b>
                                                <span>{`${i18n("it will be sent recurrently according to the cron expression")}; `}</span>
                                            </div>
                                            <div className="tooltip-row">
                                                <b>{`${i18n("Alarm Rule")}: `}</b>
                                                <span>{`${i18n("it will be sent when the alarm rule defined triggers")}.`}</span>
                                            </div>
                                        </StyledRadioTypeTooltipContainer>
                                    }
                                    onChange={currentType => {
                                        if (currentType !== scheduleType) {
                                            setScheduleType(currentType)
                                        }
                                    }}
                                    isSelected={(currentType) => currentType == scheduleType}
                                    toLabel={getScheduleTypeName}
                                />
                                {getScheduleTypeComponent({
                                    scheduleType,
                                    settings: draftSettings,
                                    updateSettings: s => setDraftSettings(s)
                                })}
                            </div>
                        </div>
                        <div className={'scheduling--recipientGroups'}>
                            <label className={'scheduling--recipientGroups__title'}>Recipient Groups</label>
                            <div className={'scheduling--recipientGroups__toggleRecipient'}>
                                <Toggle
                                    value={customRecipients}
                                    onChange={(value) => {
                                        setCustomRecipients(value)
                                    }}
                                    titleComponent={
                                        <div className={'scheduling--recipientGroups__toggleRecipient--description'}>
                                            <p className={'scheduling--recipientGroups__toggleRecipient--description__content'}>
                                                {i18n('Customize')}
                                            </p>
                                            <p className={'scheduling--recipientGroups__toggleRecipient--description__tip'}>
                                                {i18n('If not selected, this schedule will be sent to all recipient groups')}
                                            </p>
                                        </div>
                                    }
                                />
                                
                            </div>
                            <div className={'scheduling--recipientGroups__selectInput'}>
                                <SelectRecipientGroup
                                    options={allRecipients}
                                    selected={draftSettings.recipientsUid}
                                    onChange={recipientsUid => {
                                        setDraftSettings(currentJob => ({
                                            ...currentJob,
                                            recipientsUid
                                        }))
                                    }}
                                    editMode={customRecipients}
                                    keepAllGroupsSelected={
                                        customRecipients !== undefined ? !customRecipients : undefined
                                    }
                                    title={i18n('Groups Selection')}
                                    placeholder={i18n('Select or search groups')}
                                    required={true}
                                />
                            </div>
                        </div>
                    </div>
                </StyledScheduling>
            </StyledLiveModal.Body>
            <StyledLiveModal.Footer>
                <StyledLiveModal.ModalActions
                    onSubmit={() => {
                        if (!validations.length) {
                            onSave({ ...draftSettings })
                            GlobalNotification.success(<Notification title={i18n('Success adding new schedule')} />, 3)

                            onClose(false)
                        } else {
                            GlobalNotification.error(
                                <Notification title={i18n('Before saving, you need to fix the following issues: ')}>
                                    {i18n('The following entered fields are invalid: ')}
                                    {validations.map((value, index) => (
                                        <tr key={index}>{value}</tr>
                                    ))}
                                </Notification>,
                                3
                            )
                        }
                    }}
                    submitText={i18n('Save')}
                    submitBtnClassName="btn btn-primary"
                    cancelBtnClassName={null}
                    onCancel={() => {
                        onClose(false)
                    }}
                    cancelText={i18n('Cancel')}
                />
            </StyledLiveModal.Footer>
        </StyledLiveModal>
    )
}

export const StyledRadioTypeTooltipContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-direction: column;
    font-family: ${({ theme }) => theme['font-regular']};
`

export  const StyledLiveModal = styled(LiveModal)`
    .modal-dialog {
        width: 1050px;

        .modal-body {
            min-height: 500px;
        }

        .modal-footer {
            /* height: 50px; */
        }
    }

    .settings {
        font-style: normal;
        font-weight: 600;
        font-size: 16px;
        line-height: 16px;
    }

    .statusDiv {
        width: 100px;
        padding-top: 24px;
        padding-bottom: 10px;

        > label {
            padding-bottom: 8px;
        }
        .toggleStatus {
            display: flex;
            align-items: center;
            text-align: center;

            > p {
                margin: 0;
            }
        }
    }
`


const StyledScheduling = styled.div<{
    dateDisabled: boolean
    cronDisabled: boolean
    recipientGroupsDisabled: boolean
}>`
    .ScrollbarsCustom-Track,
    ScrollbarsCustom-TrackY {
        background: ${props =>
            props.recipientGroupsDisabled
                ? `rgb(245, 245, 245) none repeat scroll 0% 0%`
                : `rgb(255, 255, 255) none repeat scroll 0% 0% !important`};
        .ScrollbarsCustom-Thumb,
        ScrollbarsCustom-ThumbY {
            background: ${({ theme }) => theme['gray-400']};
            width: 5px !important;
        }
    }
    .react-datepicker__input-container {
        width: 100%;
    }

    .input-group {
        display: flex;
        cursor: pointer;
        .input-group-addon {
            display: flex;
            align-items: center;
            height: 90%;
            color: ${props => (props.dateDisabled ? props.theme['gray-400'] : props.theme['blue-500'])};
            // TODO: resolve !important
            background-color: unset !important;
        }
        input {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 40px;
        }
    }
    .scheduling {
        display: grid;
        grid-template-columns: 45% 55%;
        &--scheduleInfo {
            display: flex;
            flex-direction: column;
            width: auto;
            height: auto;
            &__timespanSection {
                margin-top: 15px;
                &--timespan {
                    /* max-width: 70%; */
                }
                &--bottom {
                    display: flex;
                    flex-direction: row;
                    align-content: center;
                    margin-left: 2px;
                    margin-top: 5px;

                    span {
                        margin-right: 5px;
                        color: ${({ theme }) => theme['gray-700']};
                    }
                }
            }

            &__schedulingSection {
                margin-top: 15px;
                display: flex;
                flex-direction: column;
                &--title {
                    font-size: 14px;
                    margin-bottom: 7px;
                    margin-top: 3px;
                }
                &--dateSelect {
                    width:100%;
                    &__datePicker {
                        display: flex;
                        flex-direction: column;
                        > label {
                            margin-bottom: 5px;
                        }
                    }
                }
                &--toggleRecurrence {
                    display: flex;
                    flex-direction: row;
                    margin-top: 25px;
                    &--nextExecution {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        margin-left: 5px;
                        margin-top: 8px;

                        &__title {
                            font-size: 13px;
                            color: ${({ theme }) => theme['gray-900']};
                        }
                        &__content {
                            color: ${({ theme }) => theme['gray-700']};
                            font-style: italic;
                            line-height: 0px;
                            font-size: 12px;
                            > div {
                                padding: 8px 0 16px 0;
                                > span {
                                    font-weight: 600;
                                    font-style: normal;
                                }
                            }
                        }
                    }
                }

                &--cronExpression {
                    min-width: 100%;
                    margin-top: 10px;
                    max-width: 70%;
                    background: var(--gray-0);
                    border: 1px solid var(--gray-400);
                    border-radius: 3px;
                }

                &--activation {
                    margin-top: 10px;
                }
            }
        }

        &--recipientGroups {
            border-left: 1px solid ${({ theme }) => theme['gray-400']};
            margin-left: 35px;
            display: flex;
            flex-direction: column;
            padding-left: 33px;
            padding-top: 15px;
            &__title {
                margin-bottom: 15px;
            }

            &__toggleRecipient {
                display: flex;
                flex-direction: row;
                &--description {
                    margin-top: 16px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    &__content {
                        color: ${({ theme }) => theme['gray-800']};
                        line-height: 0px;
                    }
                    &__tip {
                        color: ${({ theme }) => theme['gray-600']};
                        font-style: italic;
                    }
                }
            }

            &__selectInput {
                &--title {
                    margin-bottom: -8px;
                }
                &--stats {
                    display: flex;
                    flex-direction: row;
                    margin-top: 8px;
                    &__content {
                        margin-right: 5px;
                        b {
                            color: ${({ theme }) => theme['gray-800']};
                            font-family: ${({ theme }) => theme['font-bold']};
                        }
                    }
                    &__selectOptions {
                        color: ${props =>
                            props.recipientGroupsDisabled ? props.theme['gray-900'] : props.theme['blue-500']};
                        margin-left: 6px;
                        cursor: pointer;
                        &:hover {
                            color: ${props =>
                                props.recipientGroupsDisabled ? props.theme['gray-900'] : props.theme['blue-700']};
                        }
                    }
                }
            }
        }
    }
`
